import { MonetizationType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { transformContextToLinkToContext } from '@/helpers/tracking/tracking-helper';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';

const jwtvTitleRentalModal = () => import('@/features/jwtv-titles-rental/components/JwtvTitleRentalModal.vue');

type JWTVModalProps = (
	| {
			title: TitleDetail;
			offer: TitleOfferFragment | WatchNowOfferFragment;
			overrideSuccessUrl?: string | null;
			promoCode?: string;
			titleSlug?: string;
	  }
	| {
			finalUrl: string;
	  }
) & {
	additionalContexts?: SnowplowContext[];
};

export async function openJwTvRentalModal(payload: { modalProps: JWTVModalProps; onClose?: () => void }) {
	const additionalContexts = payload?.modalProps.additionalContexts?.map(context =>
		transformContextToLinkToContext(context)
	);

	await ModalHelper.openModal(
		jwtvTitleRentalModal,
		{
			...payload.modalProps,
			additionalContexts,
		},
		{
			cssClass: 'account-modal',
			id: 'jwtv-rent-modal',
		},
		undefined,
		undefined,
		additionalContexts,
		() => {
			payload.onClose?.();
		}
	);
}

export function getJWTVTitleUrl(fullPath: string, linkContextId?: string, expCode?: string): string | null {
	if (!fullPath) {
		return null;
	}
	// For local development, we need to use the localhost domain
	const queryParams = `?package=jwt${linkContextId ? `&lcId=${linkContextId}` : ''}${
		expCode ? `&expPC=${expCode}` : ''
	}`;
	if (process.client && window.location.hostname === 'localhost') {
		return `http://localhost:8080${fullPath}${queryParams}`;
	}

	const baseUrl = `https://www.${JW_CONFIG.DOMAIN}`;
	return `${baseUrl}${fullPath}${queryParams}`;
}

export function getJWTVQueryParams(routerQuery: Record<string, string>) {
	const params: {
		packageShortName?: string;
		linkContextId?: string;
		expPromoCode?: string;
		titleSlug?: string;
	} = {};
	if (routerQuery['package'] === 'jwt') {
		params['packageShortName'] = 'jwt';
	}
	if (routerQuery['lcId']) {
		params['linkContextId'] = routerQuery['lcId'];
	}

	if (routerQuery['expPC']) {
		params['expPromoCode'] = routerQuery['expPC'];
	}
	if (routerQuery['play']) {
		params['titleSlug'] = routerQuery['play'];
	}
	return params;
}

export function isJWTVRentalOffer(offer: TitleOfferFragment | WatchNowOfferFragment): boolean {
	const isJustWatchTvPackage = offer?.package.technicalName === 'justwatchtv';
	const isBuyOrRent = [MonetizationType.Buy, MonetizationType.Rent].includes(offer.monetizationType);
	const isTvStreamUrl = !!offer.streamUrl?.includes('tv.justwatch.com');

	return isJustWatchTvPackage && (isBuyOrRent || isTvStreamUrl);
}
